import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function TransactionModal({ transaction, categories, onSave, onClose }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    merchant: '',
    type: 'expense',
    category_id: '',
    amount: '',
    description: '',
    split_category_id: '',
    split_amount: '',
    verified: false,
    is_recurring: false,
  });
  const [merchants, setMerchants] = useState([]);
  const [showMerchantSuggestions, setShowMerchantSuggestions] = useState(false);
  const [recurringTransactions, setRecurringTransactions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    loadMerchants();
    if (!transaction) {
      // Only load recurring transactions when adding new transaction
      loadRecurringTransactions();
    }
    
    if (transaction) {
      setFormData({
        date: transaction.date,
        merchant: transaction.merchant,
        type: transaction.type,
        category_id: transaction.category_id,
        amount: transaction.amount,
        description: transaction.description || '',
        split_category_id: transaction.split_category_id || '',
        split_amount: transaction.split_amount || '',
        verified: !!transaction.verified,
        is_recurring: !!transaction.is_recurring,
      });
    } else if (categories.length > 0) {
      const firstExpense = categories.find(c => c.type === 'expense');
      if (firstExpense) {
        setFormData(prev => ({ ...prev, category_id: firstExpense.id }));
      }
    }
  }, [transaction, categories]);

  async function loadMerchants() {
    try {
      const data = await api.getRecentMerchants();
      setMerchants(data);
    } catch (error) {
      console.error('Failed to load merchants:', error);
    }
  }

  async function loadRecurringTransactions() {
    try {
      // Get all transactions and filter for recurring ones
      const allTransactions = await api.getTransactions('');
      const recurring = allTransactions.filter(t => t.is_recurring);
      
      // Group by merchant to get unique recurring transaction templates
      const uniqueRecurring = [];
      const seen = new Set();
      
      recurring.forEach(t => {
        const key = `${t.merchant}-${t.category_id}-${t.amount}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueRecurring.push(t);
        }
      });
      
      // Sort alphabetically by merchant name
      uniqueRecurring.sort((a, b) => a.merchant.localeCompare(b.merchant));
      
      setRecurringTransactions(uniqueRecurring);
    } catch (error) {
      console.error('Failed to load recurring transactions:', error);
    }
  }

  function handleTemplateSelect(templateId) {
    setSelectedTemplate(templateId);
    
    if (!templateId) {
      // Reset form if "None" is selected
      setFormData({
        date: new Date().toISOString().split('T')[0],
        merchant: '',
        type: 'expense',
        category_id: categories.length > 0 ? categories.find(c => c.type === 'expense')?.id || '' : '',
        amount: '',
        description: '',
        split_category_id: '',
        split_amount: '',
        verified: false,
        is_recurring: false,
      });
      return;
    }
    
    const template = recurringTransactions.find(t => t.id === parseInt(templateId));
    if (template) {
      setFormData({
        date: new Date().toISOString().split('T')[0], // Keep today's date
        merchant: template.merchant,
        type: template.type,
        category_id: template.category_id,
        amount: template.amount,
        description: template.description || '',
        split_category_id: template.split_category_id || '',
        split_amount: template.split_amount || '',
        verified: false, // Don't auto-verify
        is_recurring: true, // Mark as recurring
      });
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.merchant.trim()) {
      alert('Please enter a merchant name');
      return;
    }
    if (!formData.category_id) {
      alert('Please select a category');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) === 0) {
      alert('Please enter a valid amount (positive for spending, negative for refund)');
      return;
    }
    if (formData.split_category_id && (!formData.split_amount || parseFloat(formData.split_amount) <= 0)) {
      alert('Please enter a valid split amount');
      return;
    }
    
    const data = {
      ...formData,
      amount: parseFloat(formData.amount),
      split_amount: formData.split_amount ? parseFloat(formData.split_amount) : null,
      split_category_id: formData.split_category_id || null,
    };
    
    onSave(data);
  }

  function handleMerchantChange(value) {
    setFormData({ ...formData, merchant: value });
    setShowMerchantSuggestions(true);
  }

  function selectMerchant(merchant) {
    setFormData({ ...formData, merchant });
    setShowMerchantSuggestions(false);
  }

  const filteredMerchants = merchants.filter(m =>
    m.toLowerCase().includes(formData.merchant.toLowerCase())
  ).slice(0, 5);

  const currentCategory = categories.find(c => c.id === parseInt(formData.category_id));
  const availableSplitCategories = categories.filter(c => 
    c.type === formData.type && c.id !== parseInt(formData.category_id)
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{transaction ? 'Edit Transaction' : 'Add Transaction'}</h2>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {!transaction && recurringTransactions.length > 0 && (
            <div className="form-group recurring-template-selector">
              <label>Use Recurring Transaction Template (Optional)</label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="input-select"
              >
                <option value="">-- Create New Transaction --</option>
                {recurringTransactions.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.merchant} - ${t.amount} ({t.category_name})
                  </option>
                ))}
              </select>
              <small className="form-hint">
                Select a recurring transaction to auto-fill the form
              </small>
            </div>
          )}

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="input-text"
            />
          </div>

          <div className="form-group">
            <label>Merchant</label>
            <div className="autocomplete-wrapper">
              <input
                type="text"
                value={formData.merchant}
                onChange={(e) => handleMerchantChange(e.target.value)}
                onFocus={() => setShowMerchantSuggestions(true)}
                className="input-text"
                placeholder="Merchant name"
                autoFocus
              />
              {showMerchantSuggestions && filteredMerchants.length > 0 && (
                <div className="autocomplete-suggestions">
                  {filteredMerchants.map((merchant, idx) => (
                    <div
                      key={idx}
                      className="autocomplete-item"
                      onClick={() => selectMerchant(merchant)}
                    >
                      {merchant}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Type</label>
            <select
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value;
                const firstCategory = categories.find(c => c.type === newType);
                setFormData({
                  ...formData,
                  type: newType,
                  category_id: firstCategory ? firstCategory.id : '',
                  split_category_id: '',
                  split_amount: '',
                });
              }}
              className="input-select"
            >
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              className="input-select"
            >
              <option value="">Select category</option>
              {categories
                .filter(c => c.type === formData.type)
                .map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="input-text"
              placeholder="0.00 (negative for refund/reimbursement)"
            />
            <small className="form-hint">
              Positive = expense/income | Negative = refund/reimbursement received
            </small>
          </div>

          <div className="form-group">
            <label>Description (optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-textarea"
              rows="2"
              placeholder="Add a note about this transaction"
            />
          </div>

          <div className="form-group">
            <label>Split with Category (optional)</label>
            <select
              value={formData.split_category_id}
              onChange={(e) => setFormData({ ...formData, split_category_id: e.target.value })}
              className="input-select"
            >
              <option value="">No split</option>
              {availableSplitCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {formData.split_category_id && (
            <div className="form-group">
              <label>Split Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.split_amount}
                onChange={(e) => setFormData({ ...formData, split_amount: e.target.value })}
                className="input-text"
                placeholder="0.00"
              />
            </div>
          )}

          <div className="form-group-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.verified}
                onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
              />
              <span>Mark as verified</span>
            </label>
          </div>

          <div className="form-group-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
              />
              <span>Recurring transaction</span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {transaction ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TransactionModal;
